import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { Location } from '@shared/models';
import { DeviceFormCardComponent } from '@ep/shared';
import { DeviceFormComponent } from '@app/areas/merchants/forms/device-form/device-form.component';
import { DeviceFormService } from '@app/areas/merchants/services/device-form.service';
import { DevicesApiService } from '@app/areas/merchants/services/devices.api.service';
import { LocationsApiService } from '@app/areas/merchants/services/locations.api.service';

@Component({
  selector: 'app-new-device',
  templateUrl: './new-device.component.html',
  styleUrls: ['./new-device.component.scss'],
  standalone: true,
  imports: [DeviceFormComponent, DeviceFormCardComponent, AsyncPipe, NgIf],
})
export class NewDeviceComponent implements OnInit, OnDestroy {
  private readonly deviceFormService = inject(DeviceFormService);
  private readonly devicesApiService = inject(DevicesApiService);
  private readonly locationsApiService = inject(LocationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected isSaveDisabled$ = this.deviceFormService.getForm().statusChanges.pipe(
    startWith(this.deviceFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  protected locations: Location[] = [];
  protected isSaving = false;
  private merchantId = 0;
  private subs = new Subscription();

  ngOnInit(): void {
    this.deviceFormService.initializeForAdd();

    this.subs.add(
      this.route
        .parent!.parent!.paramMap.pipe(map((params) => Number(params.get('merchantId'))))
        .subscribe((merchantId) => {
          this.merchantId = merchantId;
          this.loadLocations(merchantId);
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadLocations(merchantId: number): void {
    this.subs.add(
      this.locationsApiService.getByMerchantId(merchantId).subscribe((locations) => {
        this.locations = locations;
      })
    );
  }

  protected saveDevice(): void {
    const form = this.deviceFormService.getForm();
    const locationId = this.locations[0]?.LocationId;
    if (!form.valid || !locationId) {
      form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const formValue = form.getRawValue();

    const request$ = formValue.SerialNumber
      ? this.devicesApiService.createPreRegistration(this.merchantId, {
          Name: formValue.Name!,
          SerialNumber: formValue.SerialNumber,
          LocationId: locationId,
        })
      : this.devicesApiService.create(this.merchantId, {
          Name: formValue.Name!,
          AuthorizationCode: formValue.AuthorizationCode!,
          LocationId: locationId,
        });

    this.subs.add(
      request$.subscribe({
        next: (device) => {
          this.isSaving = false;
          if (device) {
            this.router.navigate(['../', device.DeviceId], { relativeTo: this.route });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: New Device',
              detail: 'Something went wrong while creating the device',
            });
          }
        },
        error: (err) => {
          this.isSaving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: New Device',
            detail: err?.message ?? 'Something went wrong while creating the device',
          });
        },
      })
    );
  }
}
